(() => {
  'use strict';
  if (window.AquastarMotion) return;
  const auto = document.currentScript?.dataset.aqAuto === 'index';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px), (pointer: coarse)');
  const revealSelector = '.scroll-reveal,.scroll-reveal-left,.scroll-reveal-right,' +
    '.scroll-reveal-image,.scroll-section,[data-aqm]';
  const protectedSelector = '[data-aq-motion-ignore],aquastar-scroll-hero,.aq-hero,' +
    'header,nav,dialog,[role="dialog"],form,input,select,textarea,' +
    '.shopify-section-group-header-group,.shopify-app-block,[id^="shopify-block-"],' +
    '[class*="jdgm"],.aqr-review-apps,product-form,product-info,' +
    'slider-component,slideshow-component,slideshow-slides,.swiper,.splide,' +
    '.slick-slider,[aria-roledescription="carousel"],[data-slider]';
  const seen = new WeakSet();
  const waiting = new Set();
  const running = new Map();
  const depth = new Map();
  const activeDepth = new Set();
  let revealObserver, depthObserver, frame = 0, stopped = false;
  const supported = 'IntersectionObserver' in window &&
    typeof Element.prototype.animate === 'function' && CSS.supports('translate', '0 1px');
  const query = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)
  ];
  const safe = el => !el.closest(protectedSelector) && !el.querySelector(protectedSelector);
  const number = (style, name, fallback) => {
    const n = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(n) ? n : fallback;
  };
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const available = el => {
    const s = getComputedStyle(el);
    return s.display !== 'none' && s.visibility === 'visible' &&
      Number(s.opacity) === 1 && s.transform === 'none' &&
      s.translate === 'none' && s.scale === 'none' && !el.getAnimations().length;
  };

  function tagAquastar(root) {
    if (!auto) return;
    query(root, '.aqr').forEach(section => {
      const mark = (selector, kind) => section.querySelectorAll(selector).forEach(el => {
        if (safe(el) && !el.matches(revealSelector)) el.dataset.aqm = kind;
      });
      mark('.aqr-facts-list > li,.aqr-journal-card', 'item');
      mark('.aqr-title,.aqr-card-title', 'heading');
      mark('.aqr-eyebrow,.aqr-lead,.aqr-copy,.aqr-note,.aqr-price,.aqr-subscribe-price', 'text');
      mark('.aqr-button', 'button');
      mark('.aqr-story-image,.aqr-feature-media', 'image');
      section.querySelectorAll('.aqr-facts-list,.aqr-journal-grid').forEach(el => {
        el.classList.add('scroll-stagger');
      });
      section.querySelectorAll('.aqr-feature-media img').forEach(el => {
        if (safe(el)) el.classList.add('scroll-parallax');
      });
    });
  }

  function finish(el) {
    seen.add(el);
    waiting.delete(el);
    revealObserver?.unobserve(el);
    running.get(el)?.cancel();
    running.delete(el);
  }

  function reveal(el) {
    finish(el);
    if (reduce.matches || !safe(el) || !available(el) || el.contains(document.activeElement)) return;
    const s = getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    const image = el.matches('.scroll-reveal-image,[data-aqm="image"]');
    const section = el.matches('.scroll-section');
    let y = section || image ? 0 : clamp(number(s, '--aqm-distance', 28), 0, mobile.matches ? 14 : 40);
    let x = 0;
    if (!mobile.matches && el.matches('.scroll-reveal-left,.scroll-reveal-right')) {
      const left = el.matches('.scroll-reveal-left');
      const room = left ? rect.left : document.documentElement.clientWidth - rect.right;
      x = (left ? -1 : 1) * Math.min(24, Math.max(0, room - 2));
      y = 0;
    }
    let delay = number(s, '--aqm-delay', 0);
    const group = el.closest('.scroll-stagger');
    if (group) {
      const items = [...group.querySelectorAll(revealSelector)].filter(item =>
        item.closest('.scroll-stagger') === group && !item.parentElement.closest(revealSelector));
      delay += Math.max(0, items.indexOf(el)) * (mobile.matches ? 80 : 120);
    }
    const animation = el.animate([
      { opacity: section ? 0.55 : 0, translate: `${x}px ${y}px`, scale: image && !mobile.matches ? '0.97' : '1' },
      { opacity: 1, translate: '0px 0px', scale: '1' }
    ], {
      duration: clamp(number(s, '--aqm-duration', 850), 600, 1200),
      delay: clamp(delay, 0, mobile.matches ? 240 : 360),
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards'
    });
    running.set(el, animation);
    const clear = () => { if (running.get(el) === animation) running.delete(el); };
    animation.finished.then(clear, clear);
  }

  function restore(el) {
    const record = depth.get(el);
    if (!record) return;
    if (record.value) el.style.setProperty('translate', record.value, record.priority);
    else el.style.removeProperty('translate');
    record.y = 0;
  }

  function tick() {
    frame = 0;
    if (stopped || reduce.matches || mobile.matches) return;
    const writes = [];
    activeDepth.forEach(el => {
      const record = depth.get(el);
      if (!record || !el.isConnected) return;
      const r = el.getBoundingClientRect();
      const parent = el.parentElement.getBoundingClientRect();
      // Stay inside the existing image container: no new clipping or layout rules.
      const roomAbove = Math.max(0, r.top - record.y - parent.top);
      const roomBelow = Math.max(0, parent.bottom - (r.bottom - record.y));
      const progress = clamp((innerHeight / 2 - (r.top - record.y + r.height / 2)) /
        ((innerHeight + r.height) / 2), -1, 1);
      const y = el.contains(document.activeElement) ? 0 :
        clamp(progress * record.travel, -Math.min(24, roomAbove), Math.min(24, roomBelow));
      writes.push([el, record, y]);
    });
    writes.forEach(([el, record, y]) => {
      el.style.setProperty('translate', `0 ${y.toFixed(2)}px`);
      record.y = y;
    });
  }
  function schedule() {
    if (!frame && activeDepth.size && !stopped && !reduce.matches && !mobile.matches) {
      frame = requestAnimationFrame(() => { try { tick(); } catch (_) { destroy(); } });
    }
  }

  function refresh(root = document) {
    if (stopped || !supported) return;
    tagAquastar(root);
    if (reduce.matches) return;
    query(root, revealSelector).forEach(el => {
      if (seen.has(el) || waiting.has(el) || !safe(el) || el.matches('.scroll-parallax') ||
        el.parentElement.closest(revealSelector)) return;
      const r = el.getBoundingClientRect();
      // Do not hide content already on screen, or replay content above a restored scroll position.
      if (r.top < innerHeight || el.contains(document.activeElement)) { seen.add(el); return; }
      waiting.add(el);
      revealObserver.observe(el);
    });
    if (!mobile.matches) query(root, 'img.scroll-parallax').forEach(el => {
      if (depth.has(el) || !safe(el) || !available(el)) return;
      depth.set(el, {
        value: el.style.getPropertyValue('translate'), priority: el.style.getPropertyPriority('translate'),
        y: 0, travel: clamp(number(getComputedStyle(el), '--aqm-travel', 16), -24, 24)
      });
      depthObserver.observe(el);
    });
    schedule();
  }

  function preferences() {
    waiting.forEach(el => revealObserver.unobserve(el));
    waiting.clear();
    [...running.keys()].forEach(finish);
    depth.forEach((_, el) => restore(el));
    depth.clear();
    activeDepth.clear();
    depthObserver?.disconnect();
    refresh();
  }
  function unload(event) {
    const root = event.target;
    [...waiting, ...running.keys()].forEach(el => { if (root.contains(el)) finish(el); });
    depth.forEach((_, el) => {
      if (root.contains(el)) { restore(el); depthObserver.unobserve(el); depth.delete(el); activeDepth.delete(el); }
    });
  }
  function focus(event) {
    [...waiting, ...running.keys()].forEach(el => {
      if (el === event.target || el.contains(event.target) || event.target.contains(el)) finish(el);
    });
  }
  function load(event) { try { refresh(event.target); } catch (_) { destroy(); } }
  function destroy() {
    stopped = true;
    cancelAnimationFrame(frame);
    revealObserver?.disconnect();
    depthObserver?.disconnect();
    [...running.keys()].forEach(finish);
    depth.forEach((_, el) => restore(el));
    waiting.clear(); depth.clear(); activeDepth.clear();
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('load', schedule);
    reduce.removeEventListener('change', preferences);
    mobile.removeEventListener('change', preferences);
    document.removeEventListener('focusin', focus);
    document.removeEventListener('shopify:section:load', load);
    document.removeEventListener('shopify:section:unload', unload);
    document.removeEventListener('shopify:section:select', focus);
    document.removeEventListener('shopify:block:select', focus);
    document.removeEventListener('shopify:section:reorder', schedule);
  }
  function start() {
    if (!supported) return;
    try {
      revealObserver = new IntersectionObserver(entries => {
        try { entries.forEach(entry => {
          // Tall content uses the same 15% trigger relative to the viewport.
          const trigger = Math.min(entry.boundingClientRect.height, innerHeight) * 0.15;
          if (entry.isIntersecting && entry.intersectionRect.height >= trigger - 1) reveal(entry.target);
        }); } catch (_) { destroy(); }
      }, { threshold: [0, 0.05, 0.1, 0.15, 0.25, 0.5, 0.75, 1] });
      depthObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) activeDepth.add(entry.target);
          else activeDepth.delete(entry.target);
        });
        schedule();
      }, { rootMargin: '100px 0px' });
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      window.addEventListener('load', schedule, { once: true });
      reduce.addEventListener('change', preferences);
      mobile.addEventListener('change', preferences);
      document.addEventListener('focusin', focus);
      document.addEventListener('shopify:section:load', load);
      document.addEventListener('shopify:section:unload', unload);
      document.addEventListener('shopify:section:select', focus);
      document.addEventListener('shopify:block:select', focus);
      document.addEventListener('shopify:section:reorder', schedule);
      refresh();
    } catch (_) { destroy(); }
  }
  window.AquastarMotion = { refresh, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();